import { useEffect, useRef, useState } from 'react';
import type { Profile } from '../../lib/types';
import { printPdf, profilePdfFilename, savePdf } from '../../lib/pdfFile';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';

// Browsers without a built-in PDF viewer (Android Chrome) would download the
// file instead of showing or printing it, so they only offer Download.
const canPreviewInline = navigator.pdfViewerEnabled !== false;

export function PdfViewer({ profile, open, onClose }: { profile: Profile; open: boolean; onClose: () => void }) {
  const [withPhotos, setWithPhotos] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [pdf, setPdf] = useState<{ url: string; withPhotos: boolean } | null>(null);
  const [failed, setFailed] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let url = '';
    setPdf(null); setFailed(false); setFrameLoaded(false);
    import('../../lib/profilePdf')
      .then(({ renderProfilePdf }) => renderProfilePdf(profile, withPhotos))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPdf({ url, withPhotos });
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [open, profile, withPhotos, attempt]);

  const print = () => {
    if (pdf) printPdf(frame.current?.contentWindow, pdf.url, (href) => { window.open(href, '_blank'); });
  };

  return <Modal open={open} onClose={onClose} title={`PDF · ${profile.full_name}`} wide>
    <div className="pdf-viewer">
      <div className="pdf-viewer__toolbar">
        <div className="segmented" role="group" aria-label="PDF contents">
          <button type="button" aria-pressed={withPhotos} onClick={() => setWithPhotos(true)}>With photographs</button>
          <button type="button" aria-pressed={!withPhotos} onClick={() => setWithPhotos(false)}>Details only</button>
        </div>
        <div className="pdf-viewer__actions">
          <Button variant="secondary" icon={<Icon name="download"/>} disabled={!pdf}
            onClick={() => { if (pdf) savePdf(pdf.url, profilePdfFilename(profile.full_name, pdf.withPhotos)); }}>Download</Button>
          {canPreviewInline ? <Button icon={<Icon name="print"/>} disabled={!pdf || !frameLoaded} onClick={print}>Print</Button> : null}
        </div>
      </div>
      {failed
        ? <div className="pdf-viewer__stage"><p>The PDF could not be prepared.</p><Button variant="secondary" onClick={() => setAttempt((count) => count + 1)}>Try again</Button></div>
        : !pdf
          ? <div className="pdf-viewer__stage" role="status"><span className="spinner spinner--large" aria-hidden="true"/><p>Preparing PDF…</p></div>
          : canPreviewInline
            ? <iframe key={pdf.url} ref={frame} className="pdf-viewer__frame" src={pdf.url} title={`PDF preview of ${profile.full_name}`} onLoad={() => setFrameLoaded(true)}/>
            : <div className="pdf-viewer__stage"><p>This browser cannot show or print PDFs directly. Download the PDF, then open it in your PDF app to view or print it.</p></div>}
    </div>
  </Modal>;
}
